import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '@/app/store';
import { Thread, UserMessage, AssistantMessage } from '@/types/chatTypes';

// Define the state structure for our WebSocket slice
interface WebSocketState {
  isConnected: boolean;
  currentThreadId: number | null;
  generatingThreads: number[];
}

const initialState: WebSocketState = {
  isConnected: false,
  currentThreadId: null,
  generatingThreads: [],
};

// Create a slice for WebSocket state
const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    connectionEstablished(state) {
      state.isConnected = true;
    },
    connectionClosed(state) {
      state.isConnected = false;
    },
    setCurrentThreadId(state, action: PayloadAction<number>) {
      state.currentThreadId = action.payload;
    },
    addUserMessage(state, action: PayloadAction<UserMessage>) {
      if (!state.generatingThreads.includes(action.payload.threadId)) {
        state.generatingThreads.push(action.payload.threadId);
      }
    },
    addAssistantMessage(state, action: PayloadAction<AssistantMessage>) {
      state.generatingThreads = state.generatingThreads.filter(
        id => id !== action.payload.threadId
      );
    },
  },
});

// Export actions
export const {
  connectionEstablished,
  connectionClosed,
  setCurrentThreadId,
  addUserMessage,
  addAssistantMessage,
} = websocketSlice.actions;

// Export reducer
export default websocketSlice.reducer;

// WebSocket singleton class
class WebSocketService {
  private static instance: WebSocketService;
  private socket: WebSocket | null = null;
  private dispatch: AppDispatch | null = null;
  private getState: (() => RootState) | null = null;

  private constructor() {}

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  public init(dispatch: AppDispatch, getState: () => RootState): void {
    this.dispatch = dispatch;
    this.getState = getState;
  }

  public connect(): void {
    if (this.socket) {
      this.socket.close();
    }

    this.socket = new WebSocket("wss://api.getspect.ai/thread");

    this.socket.onopen = () => {
      if (this.dispatch) {
        this.dispatch(connectionEstablished());
      }
    };

    this.socket.onclose = () => {
      if (this.dispatch) {
        this.dispatch(connectionClosed());
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    this.socket.onmessage = (event) => {
      this.handleMessage(event);
    };
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  public sendMessage(content: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error("WebSocket not connected");
      return;
    }

    if (!this.getState) {
      console.error("Redux store not initialized");
      return;
    }

    const state = this.getState();
    const threadId = state.websocket.currentThreadId;
    
    if (state.websocket.generatingThreads.includes(threadId!)) {
      console.log("This thread is already generating a response");
      return;
    }

    if (threadId === null) {
      console.error("No thread ID set");
      return;
    }

    // Create a new user message
    const userMessage: UserMessage = {
      id: Date.now(), // Temporary ID until we get a real one
      threadId: threadId,
      content: content,
      createdAt: new Date().toISOString(),
      role: "user"
    };

    // Dispatch action to add user message to UI
    if (this.dispatch) {
      this.dispatch(addUserMessage(userMessage));
    }

    // Send message to server
    const payload = {
      threadId: threadId,
      content: content
    };

    this.socket.send(JSON.stringify(payload));
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data);

      if (this.dispatch && this.getState && message.role === "assistant") {
        // Create assistant message from server response
        const assistantMessage: AssistantMessage = {
          id: Date.now(), // Temporary ID until we get a real one
          threadId: this.getState().websocket.currentThreadId || 0,
          content: message.content,
          createdAt: new Date().toISOString(),
          role: "assistant",
          citations: message.citations || []
        };

        // Dispatch action to add assistant message to UI
        this.dispatch(addAssistantMessage(assistantMessage));
      }
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  }
}

// Export the singleton instance
export const websocketService = WebSocketService.getInstance();